import {
  CommonActions,
  StackActions,
  useNavigation
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/native';
import ActivityIndicatorScreen from '../../../components/ActivityIndicatorScreen';
import AlertBox from '../../../components/AlertBox';
import { FetchError } from '../../../api/api';
import { StudyParticipant } from '../../../models/StudyParticipant';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';

const StyledViewContainer = styled.View`
  height: 100%;
`;

type QuitStudyParticipantProps<T> = FormElementProps<
  T,
  QuitStudyParticipantConfig
>;

export const QuitStudyParticipant = <T extends object>({
  element,
  stateNode
}: QuitStudyParticipantProps<T>) => {
  const navigation = useNavigation<StackNavigationProp<any>>();
  const { redirectPath, redirectFormTemplateId } = element.config || {};
  const {
    formValues,
    studyParticipantId: contextStudyParticipantId,
    emitEvent,
    runService
  } = useDynamicFormContext<{ studyParticipantId: number }, any>();
  // Match CD-65 fallback pattern: prefer formValues.studyParticipantId when
  // the form has been saved, fall back to the context value (available
  // immediately on mount). Without the fallback the quit-study flow could
  // get stuck waiting for a saved form id that never comes — same shape
  // as the AssignStudyTemplate issue CD-65 fixed.
  const studyParticipantId =
    formValues.studyParticipantId ?? contextStudyParticipantId;

  const [deleteResult, setDeleteResult] = useState<StudyParticipant>();
  const [deletingStudyParticipant, setDeletingStudyParticipant] =
    useState(false);
  const [deleteStudyParticipantError, setDeleteStudyParticipantError] =
    useState<FetchError | Error | undefined>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  // Abort the in-flight (destructive) DELETE on unmount only — leaving the
  // screen cancels the service via its AbortSignal. The previous
  // useDeleteStudyParticipant path had no AbortController and no cleanup, so
  // the DELETE kept running (and could fire) after the user navigated away.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Delete the study participant through the state-machine invoke service. The
  // service owns the API call, retry/backoff, and AbortSignal cancellation
  // (CD-207) — replacing the previous useDeleteStudyParticipant hook +
  // hasDeletedRef fire-once dance. The AbortController doubles as the
  // single-fire guard.
  useEffect(() => {
    if (!studyParticipantId) return;
    if (abortRef.current) return; // already deleting / deleted

    const controller = new AbortController();
    abortRef.current = controller;
    setDeleteStudyParticipantError(undefined);
    setDeletingStudyParticipant(true);
    // Capture stateNode.type at effect-fire time, before the await. The
    // state machine can transition during the delete request, so a
    // post-await check would return false and 'final' would never emit
    // — leaving the form stuck even though the delete succeeded.
    const wasFinal = stateNode?.type === 'final';

    (async () => {
      try {
        const result = (await runService('quitStudyParticipant', {
          data: { studyParticipantId },
          signal: controller.signal
        })) as StudyParticipant;
        if (controller.signal.aborted) return;
        setDeleteResult(result);
        if (wasFinal) {
          await emitEvent('final', { transitions: [] });
        }
      } catch (err: any) {
        if (controller.signal.aborted) return;
        // Delete failed after retries; allow a retry on a later dep change.
        abortRef.current = undefined;
        setDeleteStudyParticipantError(err);
      } finally {
        if (!controller.signal.aborted) setDeletingStudyParticipant(false);
      }
    })();
  }, [emitEvent, runService, studyParticipantId, stateNode?.type]);
  // redirect after deleting study participant
  useEffect(() => {
    if (deleteResult) {
      if (redirectPath !== 'PreviousScreen') {
        if (redirectPath === 'FormTemplate') {
          navigation.push('FormTemplate', {
            formTemplateId: redirectFormTemplateId
            // TODO: consider adding a configuration to:
            // Option 1: Start new form
            // Option 2: Load last edited form if exists
          });
          return;
        } else if (redirectPath === 'MyData') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'MyDataNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudiesNav') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav'
              }
            })
          );
          return;
        } else if (redirectPath === 'StudyComingSoon') {
          navigation.dispatch(
            StackActions.replace('DrawerNavigator', {
              screen: 'BottomTabNav',
              params: {
                screen: 'StudiesNav',
                params: {
                  screen: 'StudyComingSoon',
                  initial: false
                }
              }
            })
          );
          return;
        }
      }
      // default to the previous screen prior to the FormTemplate
      const navigationState = navigation.getState();
      let popCount = 0;
      for (let i = navigationState.routes.length - 1; i >= 0; i--) {
        if (navigationState.routes[i].name !== 'FormTemplate') {
          navigation.pop(popCount);
          return;
        }
        popCount++;
      }
      // else navigate to My Chloe
      navigation.dispatch(
        StackActions.replace('DrawerNavigator', {
          screen: 'BottomTabNav',
          params: {
            screen: 'HomeNav'
          }
        })
      );
    }
  }, [navigation, redirectPath, redirectFormTemplateId, deleteResult]);
  return (
    <StyledViewContainer>
      {deletingStudyParticipant ||
      (redirectPath && !deleteStudyParticipantError) ? (
        <ActivityIndicatorScreen backgroundColor='transparent' />
      ) : deleteStudyParticipantError ? (
        <AlertBox severity='error'>
          {deleteStudyParticipantError.message}
        </AlertBox>
      ) : (
        <AlertBox severity='error'>
          Attempting to quit the study. However, no study participant was
          specified. Please contact the study administrator.
        </AlertBox>
      )}
    </StyledViewContainer>
  );
};

interface QuitStudyParticipantConfig {
  redirectPath?: string;
  redirectFormTemplateId?: number;
}

export const QuitStudyParticipantPlugin: FormElementPlugin<
  any,
  QuitStudyParticipantConfig
> = {
  name: 'Quit Study Participant',
  type: 'QuitStudyParticipant',
  effectOnMount: true,
  element: QuitStudyParticipant as React.FC<
    FormElementProps<any, QuitStudyParticipantConfig>
  >
};
